import { IconButton, Stack, Typography, Box } from '@mui/material';
import { Close } from '@mui/icons-material';
import { BETSLIP_WIDTH } from '../../../../constants';
import BetslipBetCount from '../../../atoms/BetslipBetCount';
import { FormattedBalance } from '../../../atoms/FormattedBalance';
import BetslipMenu from '../../../molecules/menus/BetslipMenu';
import { StyleObj } from '../../../../@types';
import { WalletType } from '../../../../types/enums';
import { useBetslipMenuOptions } from '../../../../hooks/useBetslip';
import useOddsFormat from '../../../../hooks/useOddsFormat';

import { mergeSx } from '../../../../utils/styles';

type BetslipHeaderProps = {
  numberOfBets: number;
  totalOdds?: number;
  minOdds?: number | null;
  maxOdds?: number | null;
  selectedWallet: WalletType;
  onClose: () => void;
  onWalletChange: (value: string) => void;
  onClick?: () => void;
  isCollapsed?: boolean;
};

const styles: StyleObj = {
  closeButton: {
    borderRadius: 0,
    borderLeft: (theme) => `1px solid ${theme.palette.neutral[100]}`,
    p: 1.5,
    flexShrink: 0,
  },
  oddsContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexGrow: 1,
    flexBasis: 0,
    minWidth: 0,
    overflow: 'hidden',
    px: 1,
  },
  oddsRange: {
    minWidth: 0,
    maxWidth: '100%',
  },
  oddsText: {
    minWidth: 0,
    maxWidth: '100%',
    textAlign: 'center',
    overflowWrap: 'anywhere',
    lineHeight: 1.25,
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: 2,
    overflow: 'hidden',
  },
  walletContainer: {
    flexShrink: 0,
  },
  walletSelect: {
    mr: -1.5,
    '& .MuiSelect-select': { py: 0, minHeight: 0 },
  },
  fixedContainer: {
    position: 'fixed',
    bottom: { xs: 0, md: 32 },
    left: { xs: 0, sm: `calc(50% - ${BETSLIP_WIDTH / 2}px)` },
    width: { xs: '100%', sm: BETSLIP_WIDTH },
    zIndex: 1500,
    background: 'white',
    borderRadius: { xs: 0, sm: '4px 4px 0 0' },
    cursor: 'pointer',
  },
};

const BetslipHeader = ({
  numberOfBets,
  totalOdds,
  minOdds,
  maxOdds,
  selectedWallet,
  onClose,
  onClick,
  isCollapsed,
  onWalletChange,
}: BetslipHeaderProps) => {
  const { localizedWalletTypeOptions } = useBetslipMenuOptions();
  const { formatOddsValue } = useOddsFormat();

  const showOddsRange = minOdds != null && maxOdds != null && minOdds !== maxOdds;
  const oddsText = showOddsRange
    ? `${formatOddsValue(minOdds)} - ${formatOddsValue(maxOdds)}`
    : formatOddsValue(totalOdds ?? 0);

  return (
    <Stack
      direction='row'
      spacing={1}
      onClick={onClick}
      sx={mergeSx(
        {
          alignItems: 'center',
          justifyContent: 'space-between',
        },
        isCollapsed ? styles.fixedContainer : {}
      )}
    >
      <BetslipBetCount
        numberOfBets={numberOfBets}
        sx={{
          pl: 3,
        }}
      />
      <Box sx={styles.oddsContainer}>
        <Typography
          variant='body2'
          sx={mergeSx(
            {
              color: 'neutral.600',
            },
            styles.oddsText
          )}
        >
          {oddsText}
        </Typography>
      </Box>
      <Stack
        onClick={(e) => !isCollapsed && e.stopPropagation()}
        sx={mergeSx(
          {
            alignItems: 'end',
            justifyContent: 'end',
          },
          styles.walletContainer
        )}
      >
        <BetslipMenu
          selectedMenuItem={selectedWallet}
          onChange={onWalletChange}
          options={localizedWalletTypeOptions}
          sx={styles.walletSelect}
          disabled={isCollapsed}
        />
        <Typography
          variant='body3'
          sx={{
            color: 'neutral.600',
          }}
        >
          <FormattedBalance balanceType={selectedWallet} />
        </Typography>
      </Stack>
      <IconButton
        sx={styles.closeButton}
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
      >
        <Close />
      </IconButton>
    </Stack>
  );
};

export default BetslipHeader;
